import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getData } from "../../../../services/library";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  BarChart as BarChartIcon,
  People as PeopleIcon,
  LocalShipping as LocalShippingIcon,
} from "@mui/icons-material";
import ResumenGeneral from "./components/ResumenGeneral";
import ResumenPorCliente from "./components/ResumenPorCliente";
import TablaDetallada from "./components/TablaDetallada";
import ModalFacturacion from "./components/ModalFacturacion";
import CloseIcon from "@mui/icons-material/Close";
import DistribucionProductos from "./components/DistribucionProductos";

function PanelResumenRemitos() {
  const location = useLocation();
  let navigate = useNavigate();
  const [resumen, setResumen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalFacturacion, setModalFacturacion] = useState(false);
  const [selectedForInvoice, setSelectedForInvoice] = useState([]);
  const [renglonesRemitos, setRenglonesRemitos] = useState([]);
  const [pedidosSeleccionados, setPedidosSeleccionados] = useState([])
  const [renglonesSeleccionados, setRenglonesSeleccionados] = useState([])

  const obtenerResumen = async () => {
    setLoading(true);
    setError(null);
    try {
      await getData(
        "/envasado_pedidos/resumenentregasremito/" + location.state.id,
        setResumen,
      );
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Error al cargar los datos. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const obtenerRenglonesRemitos = async () => {
    setLoading(true);
    setError(null);
    try {
      await getData(
        "/envasado_pedidos_renglon/renglonesremitos/" + location.state.id,
        setRenglonesRemitos,
      );
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Error al cargar los datos. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    obtenerResumen();
    obtenerRenglonesRemitos();
  }, []);
  // console.log("renglonesRemitos", renglonesRemitos);

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    if (!resumen || resumen.length === 0) return null;

    const totalPedidos = resumen.length;
    const pedidosReclamados = resumen.filter((p) => p.reclamado === 1).length;

    // Agrupar por cliente
    const porCliente = resumen.reduce((acc, pedido) => {
      const key = pedido.id_cliente;
      if (!acc[key]) {
        acc[key] = {
          cliente: pedido.cliente,
          id_cliente: pedido.id_cliente,
          sucursal: pedido.sucursal_cliente,
          pedidos: 0,
          entregas: [],
        };
      }
      acc[key].pedidos++;
      acc[key].entregas.push({
        idPedido: pedido.id,
        producto: pedido.producto,
        remito: pedido.remito_entrega,
      });
      return acc;
    }, {});

    // Calcular productos únicos de resumen (pedidos)
    const productosUnicos = [...new Set(resumen.map((p) => p.producto))];

    // Agrupar por fecha
    const porFecha = resumen.reduce((acc, pedido) => {
      const fecha =
        pedido.fecha_format ||
        new Date(pedido.fecha_orden).toLocaleDateString("es-AR");
      if (!acc[fecha]) {
        acc[fecha] = { fecha, pedidos: 0 };
      }
      acc[fecha].pedidos++;
      return acc;
    }, {});

    // CALCULAR ESTADÍSTICAS CON RENGLONE
    let estadisticasRenglones = null;
    if (renglonesRemitos && renglonesRemitos.length > 0) {
      // Calcular total de productos (suma de cantidades)
      const totalProductos = renglonesRemitos.reduce((sum, renglon) => {
        return sum + (renglon.cantidad || 0);
      }, 0);

      // Agrupar productos por nombre y calcular totales
      const productosAgrupados = renglonesRemitos.reduce((acc, renglon) => {
        const nombre = renglon.nombre_producto;
        if (!acc[nombre]) {
          acc[nombre] = {
            nombre: nombre,
            cantidad: 0,
            pedidos: new Set(),
            esPromo: false,
            precio: renglon.precio,
            valor_envase: renglon.valor_envase,
          };
        }
        acc[nombre].cantidad += renglon.cantidad || 0;
        if (renglon.promo === 1) {
          acc[nombre].esPromo = true;
        }
        if (renglon.id_pedido) {
          acc[nombre].pedidos.add(renglon.id_pedido);
        }
        return acc;
      }, {});

      // Convertir a array y calcular cantidad de pedidos por producto
      const productosDetallados = Object.values(productosAgrupados).map(
        (producto) => ({
          ...producto,
          cantidadPedidos: producto.pedidos.size,
        }),
      );

      // Calcular valor total estimado (si hay precios)
      const valorTotal = renglonesRemitos.reduce((sum, renglon) => {
        if (renglon.precio && renglon.cantidad) {
          return sum + renglon.precio * renglon.cantidad;
        }
        return sum;
      }, 0);

      // Calcular valor de envases (si hay)
      const valorEnvases = renglonesRemitos.reduce((sum, renglon) => {
        if (renglon.valor_envase && renglon.cantidad) {
          return sum + renglon.valor_envase * renglon.cantidad;
        }
        return sum;
      }, 0);

      // Productos en promoción
      const productosPromo = renglonesRemitos.filter(
        (r) => r.promo === 1,
      ).length;

      estadisticasRenglones = {
        totalProductos,
        productosDetallados,
        valorTotal,
        valorEnvases,
        productosPromo,
        productosUnicosRenglones: Object.keys(productosAgrupados).length,
      };
    }

    return {
      totalPedidos,
      porCliente: Object.values(porCliente),
      productosUnicos,
      totalClientes: Object.keys(porCliente).length,
      porFecha: Object.values(porFecha),
      remitos: resumen
        .filter((p) => p.remito_entrega)
        .map((p) => p.remito_entrega),
      estadisticasRenglones,
    };
  };

  const estadisticas = calcularEstadisticas();

  const handleSeleccionarFacturacion = (pedidosIds) => {
    setSelectedForInvoice(pedidosIds);
    const pedidosSeleccionados = resumen.filter(p => pedidosIds.includes(p.id));
    setPedidosSeleccionados(pedidosSeleccionados);

    const uuidsSeleccionados = pedidosSeleccionados.map(p => p.uuid_pedido);
    const renglonesFiltrados = renglonesRemitos.filter(renglon => 
      uuidsSeleccionados.includes(renglon.uuid_pedido)
    );
    setRenglonesSeleccionados(renglonesFiltrados);
    setModalFacturacion(true);
  };

  const handleCloseModalFacturacion = () => {
    setModalFacturacion(false);
    setSelectedForInvoice([]);
  };

  const handleFacturacionExitosa = () => {
    obtenerResumen(); // Refrescar datos
    handleCloseModalFacturacion();
  };

  const onBack = () => navigate(-1);

  // console.log("pedidosSeleccionados", pedidosSeleccionados);
  
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <>
      {/* Header */}

      <AppBar
        sx={{ position: "relative" }}
        style={{ backgroundColor: "#0979b0" }}
      >
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onBack}>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Resumen general de entregas con remito -{" "}
            {location.state.razon_social}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ReceiptIcon />}
            onClick={() => {
              // Tomar TODOS los IDs del resumen
              const todosLosIds = resumen.map(p => p.id);
              
              if (todosLosIds.length > 0) {
                handleSeleccionarFacturacion(todosLosIds);
              } else {
                alert("No hay pedidos para facturar.");
              }
            }}
            sx={{ ml: 1 }}
          >
            Facturar todos los remitos
          </Button>
        </Toolbar>
      </AppBar>

      <div style={{ padding: "10px" }}>
        {estadisticas ? (
          <>
            <Grid container>
              {/* Resumen por Cliente */}
              <Grid item xs={12}>
                <Box sx={{ mt: 3 }}>
                  <ResumenPorCliente
                    clientes={estadisticas.porCliente}
                    onFacturarCliente={(idCliente) => {
                      const pedidosIds = resumen
                        .filter(
                          (p) =>
                            p.id_cliente === idCliente
                            // p.factura_consfinal !== 1,
                        )
                        .map((p) => p.id);
                      handleSeleccionarFacturacion(pedidosIds);
                    }}
                  />
                </Box>
              </Grid>

              {/* Tabla Detallada */}
              <Grid item xs={12}>
                <Box sx={{ mt: 3 }}>
                  <TablaDetallada
                    pedidos={resumen}
                    onSeleccionarFacturacion={handleSeleccionarFacturacion}
                  />
                </Box>
              </Grid>

              {/* Distribución de Productos */}
              <Grid item xs={12}>
                <Box sx={{ mt: 3 }}>
                  <DistribucionProductos
                    estadisticas={estadisticas}
                    estadisticasRenglones={estadisticas?.estadisticasRenglones}
                  />
                </Box>
              </Grid>
            </Grid>
          </>
        ) : (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <LocalShippingIcon
              sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary">
              No hay datos de entregas con remito
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No se encontraron entregas registradas con remito para mostrar en
              el resumen.
            </Typography>
          </Paper>
        )}

        {/* Modal de Facturación */}
        <ModalFacturacion
          open={modalFacturacion}
          pedidos={pedidosSeleccionados}
          cliente={location.state}
          onClose={handleCloseModalFacturacion}
          pedidosIds={selectedForInvoice}
          onSuccess={handleFacturacionExitosa}
          renglonesCompletos={renglonesSeleccionados} 
        />
      </div>
    </>
  );
}

export default PanelResumenRemitos;
